import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { Spacing, Radius } from '@constants/spacing';
import { Typography } from '@constants/typography';
import { MarketMoverCard } from './MarketMoverCard';
import { useWatchlist } from '@/hooks/useWatchlist';

export interface MarketMover {
  ticker: string;
  name: string;
  price: number;
  changePercent: number;
}

interface SwipableMarketMoversProps {
  gainers: MarketMover[];
  losers: MarketMover[];
  onMoverPress: (ticker: string) => void;
  isLoading?: boolean;
}

export const SwipableMarketMovers: React.FC<SwipableMarketMoversProps> = ({
  gainers,
  losers,
  onMoverPress,
  isLoading,
}) => {
  const [activeTab, setActiveTab] = useState<'gainers' | 'losers'>('gainers');
  const { addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlist();

  const handleWatchlistToggle = async (ticker: string, name: string) => {
    if (isInWatchlist(ticker)) {
      const success = await removeFromWatchlist(ticker);
      Alert.alert(
        success ? 'Removed' : 'Error',
        success ? `${ticker} removed from watchlist` : `Failed to remove ${ticker}`
      );
    } else {
      const success = await addToWatchlist(ticker, name);
      Alert.alert(
        success ? 'Added' : 'Error',
        success ? `${ticker} added to watchlist` : `Failed to add ${ticker}`
      );
    }
  };

  if (isLoading || (gainers.length === 0 && losers.length === 0)) {
    return null;
  }

  const activeItems = activeTab === 'gainers' ? gainers : losers;

  return (
    <View style={styles.container}>
      <View style={styles.tabContainer}>
        <Pressable
          onPress={() => setActiveTab('gainers')}
          style={[styles.tab, activeTab === 'gainers' && styles.activeTab]}
        >
          <Ionicons
            name="trending-up"
            size={16}
            color={activeTab === 'gainers' ? Colors.market.profit : Colors.text.tertiary}
          />
          <Text style={[styles.tabText, activeTab === 'gainers' && styles.activeTabText]}>
            Top Gainers
          </Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab('losers')}
          style={[styles.tab, activeTab === 'losers' && styles.activeTab]}
        >
          <Ionicons
            name="trending-down"
            size={16}
            color={activeTab === 'losers' ? Colors.market.loss : Colors.text.tertiary}
          />
          <Text style={[styles.tabText, activeTab === 'losers' && styles.activeTabText]}>
            Top Losers
          </Text>
        </Pressable>
      </View>

      <View style={styles.content}>
        {activeItems.slice(0, 5).map((item, index) => (
          <MarketMoverCard
            key={item.ticker}
            symbol={item.ticker}
            name={item.name}
            price={item.price}
            changePercent={item.changePercent}
            rank={index + 1}
            onPress={() => onMoverPress(item.ticker)}
            isInWatchlist={isInWatchlist(item.ticker)}
            onWatchlistToggle={() => handleWatchlistToggle(item.ticker, item.name)}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: Spacing.xl,
  },
  tabContainer: {
    flexDirection: 'row',
    marginHorizontal: Spacing.xl,
    marginBottom: Spacing.md,
    padding: Spacing.xs,
    borderRadius: Radius.md,
    backgroundColor: Colors.surface.base,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.sm,
    marginHorizontal: Spacing.xs,
  },
  activeTab: {
    backgroundColor: Colors.surface.raised,
    borderWidth: 1,
    borderColor: Colors.ai.accent,
  },
  tabText: {
    ...Typography.bodySmall,
    color: Colors.text.tertiary,
    fontWeight: '600',
    marginLeft: Spacing.xs,
  },
  activeTabText: {
    color: Colors.text.primary,
  },
  content: {
    marginHorizontal: Spacing.xl,
  },
});
